import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchItemCatalog,
  invalidateItemCatalogCache,
  resolveItemDisplayName,
  itemOptionLabel,
  itemOptionLabelWithBatch,
  EMPTY_ITEM_LABEL,
} from '../utils/itemDisplay';

/** Load item catalog once per mount — no Provider required. */
export default function useItemNames() {
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchItemCatalog()
      .then((c) => { if (alive) setCatalog(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const refresh = useCallback(() => {
    invalidateItemCatalogCache();
    return fetchItemCatalog({ force: true }).then((c) => {
      setCatalog(c);
      return c;
    });
  }, []);

  return useMemo(() => ({
    catalog,
    items: catalog?.items ?? [],
    resolveItem: (row) => (
      catalog ? catalog.resolve(row) : resolveItemDisplayName(row, [])
    ),
    resolveItemId: (itemId) => (
      catalog ? catalog.resolveById(itemId) : EMPTY_ITEM_LABEL
    ),
    itemOptionLabel,
    itemOptionLabelWithBatch,
    refresh,
    // Extended helpers — safe no-ops when catalog is still loading
    getBarcode:          (id) => catalog?.getBarcode(id) ?? null,
    getBaseUomCode:      (id) => catalog?.getBaseUomCode(id) ?? '',
    getPurchaseUomCode:  (id) => catalog?.getPurchaseUomCode(id) ?? '',
    getIssueUomCode:     (id) => catalog?.getIssueUomCode(id) ?? '',
    isControlled:        (id) => catalog?.isControlled(id) ?? false,
    isBatchTracked:      (id) => catalog?.isBatchTracked(id) ?? false,
    isExpiryTracked:     (id) => catalog?.isExpiryTracked(id) ?? false,
    getItemCode:         (id) => catalog?.getItemCode(id) ?? '',
    getItemClass:        (id) => catalog?.getItemClass(id) ?? '',
    getMaterialGroup:    (id) => catalog?.getMaterialGroup(id) ?? '',
  }), [catalog, refresh]);
}
